import { api } from "./api";

/* interiorLeadsApi — admin client for interior leads CRUD.
   Mirrors leadsApi pattern from Phase 2. Includes a toUi adapter
   that flattens API response (statusHistory, createdBy joins)
   to a shape closer to the existing mock data in AppDataContext. */

const BASE = "/interior-leads";

function toUi(l) {
  if (!l) return null;
  return {
    id: l.id,
    name: l.name,
    mobile: l.mobile,
    whatsapp: l.whatsapp || "",
    email: l.email || "",
    serviceType: l.serviceType,
    serviceTypeOther: l.serviceTypeOther || "",
    propertyType: l.propertyType,
    bhk: l.bhk || "",
    areaApprox: l.areaApprox || "",
    locality: l.locality || "",
    city: l.city || "",
    moveInDate: l.moveInDate ? new Date(l.moveInDate).toISOString().slice(0, 10) : "",
    budget: l.budget || "",
    budgetMin: l.budgetMin,
    budgetMax: l.budgetMax,
    requirements: l.requirements || "",
    source: l.source,
    sourceOther: l.sourceOther || "",
    status: l.status,
    temperature: l.temperature || "warm",
    assignedTo: Array.isArray(l.assignedTo) ? l.assignedTo : [],
    followUpAt: l.followUpAt || null,
    lastActivity: l.lastActivity || l.updatedAt,
    lastStatusChangeAt: l.lastStatusChangeAt || l.createdAt,
    createdById: l.createdById,
    createdAt: l.createdAt,
    updatedAt: l.updatedAt,
    statusHistory: (l.statusHistory || []).map((h) => ({
      fromStatus: h.fromStatus,
      toStatus: h.toStatus,
      changedById: h.changedById,
      reason: h.reason,
      createdAt: h.createdAt,
    })),
  };
}

function toApi(payload) {
  const out = { ...payload };
  // Drop UI-only fields
  delete out.id;
  delete out.statusHistory;
  delete out.lastActivity;
  delete out.lastStatusChangeAt;
  delete out.createdAt;
  delete out.updatedAt;
  delete out.createdById;

  // Numeric coercion
  if (out.budgetMin !== undefined && out.budgetMin !== null && out.budgetMin !== "") {
    out.budgetMin = Number(out.budgetMin);
    if (Number.isNaN(out.budgetMin)) delete out.budgetMin;
  } else {
    delete out.budgetMin;
  }
  if (out.budgetMax !== undefined && out.budgetMax !== null && out.budgetMax !== "") {
    out.budgetMax = Number(out.budgetMax);
    if (Number.isNaN(out.budgetMax)) delete out.budgetMax;
  } else {
    delete out.budgetMax;
  }

  // Drop empty optional strings to keep validation clean
  ["whatsapp", "email", "serviceTypeOther", "bhk", "areaApprox", "locality", "city",
   "moveInDate", "budget", "requirements", "sourceOther"].forEach((k) => {
    if (out[k] === "" || out[k] == null) delete out[k];
  });

  return out;
}

export const interiorLeadsApi = {
  async list(params = {}) {
    const q = new URLSearchParams();
    if (params.search) q.set("search", params.search);
    if (params.status) q.set("status", params.status);
    if (params.serviceType) q.set("serviceType", params.serviceType);
    if (params.source) q.set("source", params.source);
    if (params.assignedToUserId) q.set("assignedToUserId", params.assignedToUserId);
    if (params.sortBy) q.set("sortBy", params.sortBy);
    if (params.page) q.set("page", String(params.page));
    if (params.pageSize) q.set("pageSize", String(params.pageSize));

    const res = await api.get(`${BASE}?${q.toString()}`);
    return { ...res, items: (res.items || []).map(toUi) };
  },

  async get(id) {
    const res = await api.get(`${BASE}/${id}`);
    return toUi(res);
  },

  async create(payload) {
    const res = await api.post(BASE, toApi(payload));
    return toUi(res);
  },

  async update(id, payload) {
    const res = await api.patch(`${BASE}/${id}`, toApi(payload));
    return toUi(res);
  },

  async delete(id) {
    return api.delete(`${BASE}/${id}`);
  },
};

// ── Display labels ──────────────────────────────────────────────────

export const INTERIOR_SERVICE_LABEL = {
  full_home: "Full Home Interiors",
  modular_kitchen: "Modular Kitchen",
  living_wardrobes: "Living Room & Wardrobes",
};

export const INTERIOR_STATUS_LABEL = {
  new: "New",
  contacted: "Contacted",
  consultation_scheduled: "Consultation Scheduled",
  site_measurement: "Site Measurement",
  design_shared: "Design Shared",
  quoted: "Quoted",
  negotiating: "Negotiating",
  booked: "Booked",
  on_hold: "On Hold",
  lost: "Lost",
};

export const INTERIOR_STATUS_COLOR = {
  new: "bg-blue-100 text-blue-700 border-blue-200",
  contacted: "bg-cyan-100 text-cyan-700 border-cyan-200",
  consultation_scheduled: "bg-indigo-100 text-indigo-700 border-indigo-200",
  site_measurement: "bg-purple-100 text-purple-700 border-purple-200",
  design_shared: "bg-pink-100 text-pink-700 border-pink-200",
  quoted: "bg-amber-100 text-amber-700 border-amber-200",
  negotiating: "bg-orange-100 text-orange-700 border-orange-200",
  booked: "bg-emerald-100 text-emerald-700 border-emerald-200",
  on_hold: "bg-slate-100 text-slate-700 border-slate-200",
  lost: "bg-red-100 text-red-700 border-red-200",
};
